import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { ArrowRight, Users, Scale, Rocket, Building2, ArrowLeftRight, Shield, Stethoscope , FileSignature} from "lucide-react";
import { services } from "@/content/services";
import { EMPLOYMENT_LAW_SLUGS, HEALTH_SAFETY_SLUGS } from "@/components/mega-menu-data";
import { CTASection } from "@/components/cta-section";
import { InternalLinksHub } from "@/components/internal-links-hub";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "HR & Employment Law Services for UK Businesses" },
      {
        name: "description",
        content:
          "Outsourced HR, employment law advice, tribunal defence, redundancy and TUPE support for UK SMEs. Solicitor-backed and senior-led.",
      },
      { property: "og:title", content: "HR & Employment Law Services for UK Businesses" },
      { property: "og:description", content: "A complete people partner for ambitious UK businesses." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/services" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "HR & Employment Law Services for UK Businesses",
          url: "https://www.hrservicecentre.co.uk/services",
          description:
            "Outsourced HR, employment law advice, tribunal defence, redundancy and TUPE support for UK SMEs.",
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
          about: { "@id": "https://www.hrservicecentre.co.uk/#organization" },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: services.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://www.hrservicecentre.co.uk/services/${s.slug}`,
              name: s.name,
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Services", item: "https://www.hrservicecentre.co.uk/services" },
          ],
        }),
      },
    ],
  }),
  component: ServicesIndex,
});

const icons = {
  users: Users,
  scale: Scale,
  rocket: Rocket,
  building: Building2,
  transfer: ArrowLeftRight,
  shield: Shield,
  stethoscope: Stethoscope,
  contract: FileSignature,
} as const;

function ServicesIndex() {
  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Services", path: "/services" },
        ]}
        eyebrow="Services"
        title="HR & Employment Law Services for UK Employers"
        subtitle="A full suite of HR and employment law services, fixed monthly fees."
        intro="Senior-led HR advice and qualified employment law support, designed for UK SMEs from first hire through to tribunal defence."
        actions={
          <>
            <Link
              to="/contact-hr-experts"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-3 text-sm font-medium text-brand-foreground transition hover:brightness-110"
            >
              Get a quote
              <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/hr-packages"
              className="inline-flex items-center rounded-md border border-border bg-card px-5 py-3 text-sm font-medium text-foreground transition hover:bg-secondary"
            >
              View pricing
            </Link>
          </>
        }
        aside={
          <div className="rounded-2xl border border-border bg-card p-8 ring-1 ring-black/5">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent">Trusted by UK SMEs</p>
            <div className="mt-6 grid grid-cols-2 gap-6">
              <div>
                <p className="font-serif text-3xl text-brand">98%</p>
                <p className="mt-1 text-sm text-muted-foreground">Tribunal success rate</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-brand">2 hrs</p>
                <p className="mt-1 text-sm text-muted-foreground">Avg. response time</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-brand">1,200+</p>
                <p className="mt-1 text-sm text-muted-foreground">Businesses served</p>
              </div>
              <div>
                <p className="font-serif text-3xl text-brand">500+</p>
                <p className="mt-1 text-sm text-muted-foreground">Templates &amp; policies</p>
              </div>
            </div>
          </div>
        }
      />

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl space-y-16">
          {[
            {
              heading: "HR services",
              blurb: "Day-to-day people support, outsourcing and the documents that sit behind it.",
              items: services.filter(
                (s) =>
                  !EMPLOYMENT_LAW_SLUGS.includes(s.slug as never) && !HEALTH_SAFETY_SLUGS.includes(s.slug as never)
              ),
            },
            {
              heading: "Employment law services",
              blurb: "Solicitor-led advice, tribunal defence and the cover that pays for it.",
              items: services.filter((s) => EMPLOYMENT_LAW_SLUGS.includes(s.slug as never)),
            },
            {
              heading: "Health & safety services",
              blurb: "Audits, risk assessments and wellbeing support for UK workplaces.",
              items: services.filter((s) => HEALTH_SAFETY_SLUGS.includes(s.slug as never)),
            },
          ].map((group) => (
            <div key={group.heading}>
              <h2 className="font-serif text-2xl text-brand">{group.heading}</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">{group.blurb}</p>
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((s) => {
                  const Icon = icons[s.iconKey];
                  return (
                    <Link
                      key={s.slug}
                      to="/services/$slug"
                      params={{ slug: s.slug }}
                      className="group flex flex-col rounded-2xl bg-card p-8 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:ring-accent/30"
                    >
                      <span className="mb-6 inline-flex size-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                        <Icon className="size-5" />
                      </span>
                      <h3 className="font-serif text-xl text-brand">{s.name}</h3>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
                      <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent">
                        Explore {s.name} <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>


      <InternalLinksHub
        heading="Pair our HR services with sector expertise and free tools"
        intro="Every service is delivered alongside industry know-how, calculators and templates for UK employers."
      />
      <CTASection />
    </>
  );
}
